/**
 * This module contains all the prompts sent to the Gemini API.
 * Keeping them in a separate file makes the main application logic cleaner
 * and makes the prompts easier to edit and manage.
 */

// Prompt for Step 1: Detecting the main offer blocks from a full flyer page.
export const detectOffersPrompt = `Analyze this image of a supermarket flyer. Your task is to identify all distinct product offers based on the following rules. Output a json list of objects, with a maximum of 20 items. Each object represents one complete offer and must contain "box_2d": The 2D bounding box coordinates on a 1000x1000 grid.
**Bounding Box Rules (Very Important):**
1. **Completeness:** The "box_2d" MUST be a single, large box that completely encloses the entire offer block. This includes the product photo(s), all text descriptions, any brand logos, all associated prices, and any other visual elements related to that specific offer. **Ensure that no characters or text or a logo are cut off.** If necessary, make the box slightly larger than the visible elements to guarantee full inclusion.
2. **Multiple Products, One Offer:** If a single price or offer description applies to multiple products shown in separate photos, the bounding box must be large enough to encompass ALL the product photos and text related to that single offer.
3. **Multiple Prices, One Product:** If a single product has multiple prices, the bounding box must expand to include all of them.`;


// Prompt for Step 2: Extracting detailed product information from a single, cropped offer block.
export const extractDetailsPrompt = `Analyze this single offer image. Extract all product details into a JSON list.
**Rules:**
1.  **Name Handling :**
    -   Both 'english_name' and 'arabic_name' are REQUIRED and MUST NOT be empty.
    -   If you only find the English name, you MUST translate it to generate the 'arabic_name'.
    -   If you only find the Arabic name, you MUST translate it to generate the 'english_name'.
2.  **Price & Quantity (Very Important):**
    -   'quantity': The number of items in a pack. For "2 x 500g", this is '2'. If it's a single item, this is '1'.
    -   'size': The size of a single item, as a number. For "500g", this is '500'. For "1.5L", this is '1.5'.
3.  **Price Per Unit Calculation (Critical):**
    -   You MUST calculate 'price_per_unit'.
    -   First, convert the size to a standard unit (KG or L). (1000g = 1kg, 1000ml = 1L).
    -   Formula: \`price_per_unit = current_price / (quantity * size_in_standard_unit)\`.
    -   Example: For a "2 x 500g" pack at 10 SAR, the total size is 1 KG. The 'price_per_unit' is 10.00 .
    -   If the unit is not weight or volume (e.g., "pieces"), calculate the 'price_per_unit' as the price per piece or per quantity whatever is more convenient.
    -   You MUST also extract the standard unit used for this calculation into a new \`unit_of_measure\` field. This value should be "KG", "L", or the per-item unit (e.g., "Piece", "Item").
2.  **Multiple Products, One Price:** If one price applies to multiple products, create a separate JSON object for each product.
3.  **Multiple Variants, One Product:** If one product has multiple prices for different sizes, create a separate JSON object for each variant.
4.  **Categorization:** You MUST assign a category to each product. The value for the \`category\` field MUST be one of the following exact strings:
    * \`Fresh Food\`
    * \`Fruits & Vegetables\`
    * \`Food Cupboard\`
    * \`Beverages\`
    * \`Frozen Food\`
    * \`Bakery\`
    * \`Bio & Organic Food\`
    * \`Pet Supplies\`
    * \`Electronics & Appliances\`
    * \`Smartphones, Tablets & Wearables\`
    * \`Beauty & Personal Care\`
    * \`Automotive\`
    * \`Baby Products\`
    * \`Cleaning & Household\`
    * \`Fashion, Accessories & Luggage\`
    * \`Home & Garden\`
    * \`Kiosk\`
    * \`Health & Fitness\`
    * \`Stationery & School Supplies\`
    * \`Toys & Outdoor\`
5.  **Field Extraction:** Extract the following fields for each object. If a field is not present, use an empty string \`""\`:
    - \`category\` (Must be from the list provided in previous steps)
    - \`english_name\`
    - \`arabic_name\`
    - \`vendor_english\` (The brand's English name)
    - \`vendor_arabic\` (The brand's Arabic name)
    - \`current_price\` (The main offer price)
    - \`previous_price\` (The old, crossed-out price, if present)
    - \`currency\` (e.g., "SAR")
    - \`quantity\` (Number, e.g., 2)
    - \`size\` (Number, e.g., 500)
    - \`unit\` (String, e.g., "g")
    - \`price_per_unit\` (Number, calculated)
    - \`unit_of_measure\` (String, e.g., "KG", "L", "Piece")
    - \`extra_details_english\`
    - \`extra_details_arabic\``;


/**
 * Generates a dynamic, coded prompt for the final categorization step.
 * @param {Array<Object>} products - The list of products to be categorized.
 * @param {Object} subTree - The relevant part of the category tree.
 * @returns {string} The generated prompt for the API call.
 */
export const createCategorizationPrompt = (products, subTree) => {
    // Create a coded list of the sub-tree for the prompt
    let categoryText = '';
    Object.keys(subTree).forEach((subCategory, subIndex) => {
        const subCode = `S${subIndex + 1}`;
        categoryText += `\n${subCode}: ${subCategory}\n`;
        subTree[subCategory].forEach((leaf, leafIndex) => {
            const leafCode = `${subCode}-${leafIndex + 1}`;
            categoryText += `  ${leafCode}: ${leaf}\n`;
        });
    });

    // Create a coded list of products for the prompt
    const productText = products.map((product, index) => 
        `P${index + 1}: ${product.english_name} (${product.vendor_english || 'N/A'})`
    ).join('\n');

    return `You are a product categorization expert. Your task is to assign a specific sub-category to each product from the provided list.

**Category Tree:**
${categoryText}

**Products to Categorize:**
${productText}

**Instructions:**
Respond ONLY with a JSON array of arrays for maximum efficiency.
Each inner array must be: [ProductCode, CategoryCode].
Do not include keys or explanation.

**Example Response Format:**
[
  ["P1", "S2-3"],
  ["P2", "S1-1"]
]`;
};
